import { useEffect, useRef, useState } from "react";
import { LiaSearchSolid } from "react-icons/lia";
import { MdClose } from "react-icons/md";
import { HiOutlinePlus, HiOutlineSquares2X2, HiOutlineClock } from "react-icons/hi2";
import { FaEye } from "react-icons/fa";
import { notify } from "../../common/Toast";
import Loader from "../../common/Loader";
import Table from "../../common/Table";
import ConfirmDeleteModal from "../../common/DeleteModel";
import { DiePurchaseOrderAPI } from "../../utlis/apiService";
import DiePurchaseOrderForm from "./DiePurchaseOrderForm";
import ErrorModal from "../../common/CustomModel";
import Button from "../../common/Button";
import { formatDate } from "../../utlis/formateDate";
import PendingInwardTab from "./PendingInwardTab";

const DiePurchaseOrderMaster = () => {
    const user = JSON.parse(sessionStorage.getItem("user"));
    const userId = user?.userId;
    const today = new Date().toISOString()?.split("T")[0];
    const [activeTab, setActiveTab] = useState("po_history"); // New state for tabs
    const [isModelOpen, setIsModelOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [errorModalOpen, setErrorModalOpen] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [errorSubMessage, setErrorSubMessage] = useState("");
    const [isEditing, setIsEditing] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const [formData, setFormData] = useState({
        po_No: 0,
        po_Date: today,
        supplier_Id: "",
        delivery_Days: "",
        delivery_Date: "",
        created_By: userId,
        details: [],
    });
    const [expandedRows, setExpandedRows] = useState({});
    const [expandedData, setExpandedData] = useState({});
    const [loadingDetails, setLoadingDetails] = useState({});
    const [loading, setLoading] = useState(false);
    const [dataList, setDataList] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 50;
    const [hasMore, setHasMore] = useState(true);
    const isFetchingRef = useRef(false);
    const tableScrollRef = useRef(null);
    const [searchTerm, setSearchTerm] = useState("");

    const fetchData = async () => {
        setLoading(true);
        try {
            const res = await DiePurchaseOrderAPI.getAllWithPagination(
                currentPage,
                pageSize
            );
            if (currentPage === 1) {
                setDataList(res.data || []);
            } else {
                setDataList((prev) => [...prev, ...(res.data || [])]);
            }

            if (!res.data || res.data.length < pageSize) {
                setHasMore(false);
            } else {
                setHasMore(true);
            }

            isFetchingRef.current = false;
        } catch (error) {
            notify("Error fetching purchase orders", "error");
        }
        setLoading(false);
    };

    useEffect(() => {
        const el = tableScrollRef.current;
        if (!el) return;

        const onScroll = () => {
            if (loading || !hasMore || isFetchingRef.current) return;

            const { scrollTop, clientHeight, scrollHeight } = el;

            if (scrollHeight - scrollTop <= clientHeight + 100) {
                isFetchingRef.current = true;
                setCurrentPage((p) => p + 1);
            }
        };

        el.addEventListener("scroll", onScroll);
        return () => el.removeEventListener("scroll", onScroll);
    }, [loading, hasMore]);

    useEffect(() => {
        fetchData();
    }, [currentPage]);

    const handleAdd = () => {
        setIsModelOpen(true);
        setIsEditing(false);
        setFormData(blankForm);
    };

    const handleEdit = async (id) => {
        try {
            const res = await DiePurchaseOrderAPI.getById(id);
            if (res.data) {
                const d = res.data;
                setFormData({
                    po_No: d.po_No || d.PO_No || d.pO_No || d.id || d.Id || 0,
                    po_Date: (d.po_Date || d.PO_Date || today).split("T")[0],
                    supplier_Id: d.supplier_Id || d.Supplier_Id || "",
                    delivery_Days: d.delivery_Days || d.Delivery_Days || "",
                    delivery_Date: (d.delivery_Date || d.Delivery_Date || "").split("T")[0],
                    created_By: d.created_By || d.Created_By || userId,
                    details: (d.details || d.Details || []).map(item => ({
                        trn_No: item.trn_No || item.Trn_No || 0,
                        dieNumber: item.dieNumber || item.DieNumber || "",
                        subDieNo: item.subDieNo || item.SubDieNo || "",
                        is_Bloster: item.is_Bloster || item.Is_Bloster || false,
                        bloster_No: item.bloster_No || item.Bloster_No || "",
                        orderWeight: item.orderWeight || item.OrderWeight || "",
                        profile_Name: item.profile_Name || item.Profile_Name || "",
                        series_Name: item.series_Name || item.Series_Name || "",
                        width_Height: item.width_Height || item.Width_Height || "",
                        range_Per: item.range_Per || item.Range_Per || "",
                        area_Sqmm: item.area_Sqmm || item.Area_Sqmm || "",
                        perimeter: item.perimeter || item.Perimeter || "",
                        is_Image_Upload: item.is_Image_Upload || item.Is_Image_Upload || false,
                        file: null,
                        is_Pdf_Upload: !!(item.file || item.File || item.is_Pdf_Upload || item.Is_Pdf_Upload),
                        die_Image: item.die_Image || item.Die_Image || "",
                    }))
                });
                setIsEditing(true);
                setIsModelOpen(true);
            }
        } catch (error) {
            notify("Failed to fetch details", "error");
        }
    };

    const handleExpandItems = async (id, newState) => {
        const poNo = id; // The correct ID from Table
        setExpandedRows(prev => ({ ...prev, [poNo]: newState }));

        if (newState && !expandedData[poNo]) {
            setLoadingDetails(prev => ({ ...prev, [poNo]: true }));
            try {
                const res = await DiePurchaseOrderAPI.getDiePOItems(poNo);
                if (res?.success) {
                    setExpandedData(prev => ({ ...prev, [poNo]: res.data }));
                } else {
                    notify(res?.message || "No items found", "warning");
                }
            } catch (err) {
                notify("Failed to load item details", "error");
            } finally {
                setLoadingDetails(prev => ({ ...prev, [poNo]: false }));
            }
        }
    };

    const handleDelete = (id) => {
        setSelectedId(id);
        setIsDeleteModalOpen(true);
    };

    const blankForm = {
        po_No: 0,
        po_Date: today,
        supplier_Id: "",
        delivery_Days: "",
        delivery_Date: "",
        created_By: userId,
        details: [{
            trn_No: 0,
            dieNumber: "",
            subDieNo: "",
            is_Bloster: false,
            bloster_No: "",
            file: null,
            is_Pdf_Upload: false,
            orderWeight: "",
            profile_Name: "",
            series_Name: "",
            width: "",
            height: "",
            die_Image: "",
            imageFile: null,
            is_Image_Upload: false,
            range_Per: "",
            area_Sqmm: "",
            perimeter: ""
        }],
    };

    const handleConfirmDelete = async () => {
        setLoading(true);
        try {
            await DiePurchaseOrderAPI.delete(selectedId);
            notify("Deleted successfully", "success");
            fetchData();
        } catch (error) {
            notify("Failed to delete record", "error");
        } finally {
            setLoading(false);
            setIsDeleteModalOpen(false);
            setSelectedId(null);
        }
    };

    const handleSubmit = async (submittedData) => {
        setLoading(true);
        try {
            await DiePurchaseOrderAPI.saveOrUpdate(submittedData);
            notify("Saved successfully", "success");
            setIsModelOpen(false);
            setFormData(blankForm);
            if (currentPage === 1) fetchData();
            else setCurrentPage(1);
        } catch (err) {
            notify("Save failed", "error");
            setErrorMessage("Submission failed");
            setErrorSubMessage("Please check your input or try again.");
            setErrorModalOpen(true);
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <div>
                {/* Modern Header with Tabs */}
                <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4 bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                    <div className="flex flex-col">
                        <h2 className="text-2xl font-extrabold text-gray-800 tracking-tight">
                            Die Purchase Order
                        </h2>
                        <div className="flex bg-slate-100/80 p-1 rounded-xl mt-3 w-fit border border-slate-200/50">
                            <button 
                                onClick={() => setActiveTab("po_history")}
                                className={`text-[13px] font-bold transition-all px-4 py-2 rounded-lg flex items-center gap-2 ${activeTab === "po_history" ? "bg-white text-slate-800 shadow-sm border border-slate-200" : "text-slate-500 hover:text-slate-700"}`}
                            >
                                <HiOutlineClock className={`${activeTab === "po_history" ? "text-blue-600" : "text-slate-400"}`} size={18} />
                                PO HISTORY
                            </button>
                            <button 
                                onClick={() => setActiveTab("pending_inward")}
                                className={`text-[13px] font-bold transition-all px-4 py-2 rounded-lg flex items-center gap-2 ${activeTab === "pending_inward" ? "bg-white text-slate-800 shadow-sm border border-slate-200" : "text-slate-500 hover:text-slate-700"}`}
                            >
                                <HiOutlineSquares2X2 className={`${activeTab === "pending_inward" ? "text-blue-600" : "text-slate-400"}`} size={18} />
                                PENDING INWARD
                                {dataList.some(d => d.total_Items > 0) && (
                                    <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
                                )}
                            </button>
                        </div>
                    </div>

                    {activeTab === "po_history" && (
                        <div className="flex flex-col sm:flex-row items-center gap-3 w-full md:w-auto mt-4 md:mt-0">
                            <div className="relative w-full sm:w-72 group">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <LiaSearchSolid className="h-5 w-5 text-gray-400 group-focus-within:text-blue-500 transition-colors" />
                                </div>
                                <input
                                    type="text"
                                    placeholder="Search History..."
                                    className="block w-full pl-10 pr-10 py-2.5 bg-gray-50 border border-gray-200 text-gray-900 text-sm rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                                {searchTerm && (
                                    <button
                                        onClick={() => setSearchTerm("")}
                                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-red-500 transition-colors"
                                    >
                                        <MdClose size={18} />
                                    </button>
                                )}
                            </div>

                            <Button
                                text="Add PO"
                                className="bg-orange text-black font-bold px-6 py-2.5 rounded-xl flex items-center shadow-lg active:scale-95 transition-all"
                                icon={HiOutlinePlus}
                                onClick={handleAdd}
                            />
                        </div>
                    )}
                </div>

                {activeTab === "po_history" ? (
                    <div
                        ref={tableScrollRef}
                        className="relative overflow-auto shadow-inner rounded-xl border border-slate-50"
                        style={{ height: "calc(100vh - 260px)" }}
                    >
                        {loading && currentPage === 1 ? (
                            <Loader />
                        ) : (
                            <div className="min-w-max">
                                <Table
                                    columns={[
                                        { key: "pO_No", label: "PO No" },
                                        { key: "pO_Date", label: "PO Date", render: (val) => formatDate(val) },
                                        { key: "supplier_Name", label: "Supplier" },
                                        {
                                            key: "total_Items",
                                            label: "Items",
                                            render: (val, row) => (
                                                <div
                                                    className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-xs transition-all shadow-sm mx-auto ${expandedRows[row.pO_No || row.PO_No] ? 'bg-green-600 text-white' : 'bg-green-100 text-green-700'}`}
                                                >
                                                    {val || row.Total_Items || 0}
                                                </div>
                                            )
                                        },
                                        { key: "delivery_Date", label: "Delivery Date", render: (val) => formatDate(val) },
                                        { key: "delivery_Days", label: "Delivery Days" },
                                    ]}
                                    data={dataList.filter((item) =>
                                        !searchTerm ||
                                        item.supplier_Name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                        item.pO_No?.toString().includes(searchTerm)
                                    )}
                                    tableClassName="text-sm"
                                    rowIdField="pO_No"
                                    expandedRows={expandedRows}
                                    onExpand={handleExpandItems}
                                    onEdit={(row) => handleEdit(row.pO_No || row.PO_No)}
                                    onDelete={(row) => handleDelete(row.pO_No || row.PO_No)}
                                    renderExpansion={(row) => {
                                        const poNo = row.pO_No || row.PO_No;
                                        const items = expandedData[poNo] || [];
                                        const isLoading = loadingDetails[poNo];

                                        if (isLoading) return <div className="p-10 flex flex-col items-center justify-center gap-3 text-blue-600 font-bold animate-pulse"><Loader size="sm" /><span>Fething Items...</span></div>;
                                        if (items.length === 0) return <div className="p-8 text-center text-gray-500 italic bg-slate-50 mx-6 rounded-2xl border-2 border-dashed border-slate-200">No items recorded for this order.</div>;

                                        return (
                                            <div className="bg-white p-6 rounded-3xl border border-blue-100 shadow-2xl mx-6 mb-6 overflow-hidden mt-2 border-t-4 border-t-blue-500">
                                                <div className="flex justify-between items-center mb-4">
                                                    <h4 className="text-sm font-black text-slate-800 uppercase tracking-tighter">Order Details: PO #{poNo}</h4>
                                                    <span className="px-3 py-1 bg-blue-50 text-blue-600 text-[10px] font-bold rounded-full">{items.length} Items</span>
                                                </div>
                                                <table className="w-full text-xs">
                                                    <thead className="bg-slate-50 text-slate-400 uppercase font-black tracking-widest border-b border-slate-100">
                                                        <tr>
                                                            <th className="p-4 text-left">Die Number</th>
                                                            <th className="p-4 text-left">Profile</th>
                                                            <th className="p-4 text-left">Series</th>
                                                            <th className="p-4 text-right">Order Wt</th>
                                                            <th className="p-4 text-center">Width x Height</th>
                                                            <th className="p-4 text-center">Bloster</th>
                                                            <th className="p-4 text-center">Assets</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="divide-y divide-slate-50">
                                                        {items.map((item, i) => {
                                                            const dieStr = item.dieNumber || "";
                                                            const lastIndex = dieStr.lastIndexOf('-');
                                                            const diePart = lastIndex !== -1 ? dieStr.substring(0, lastIndex) : dieStr;
                                                            const subPart = lastIndex !== -1 ? dieStr.substring(lastIndex + 1) : "";
                                                            const fileNameBase = `${poNo}_${diePart}${subPart ? '_' + subPart : ''}`;

                                                            return (
                                                                <tr key={i} className="group hover:bg-blue-50/20 transition-all duration-300">
                                                                    <td className="p-4 font-black text-blue-600 text-sm whitespace-nowrap">
                                                                        <div className="flex items-center gap-2">
                                                                            <span className="w-1.5 h-1.5 rounded-full bg-blue-400 group-hover:scale-150 transition-transform"></span>
                                                                            {item.dieNumber}
                                                                        </div>
                                                                    </td>
                                                                    <td className="p-4 text-slate-700 font-bold italic">{item.profile_Name}</td>
                                                                    <td className="p-4 text-slate-500 font-medium">{item.series_Name}</td>
                                                                    <td className="p-4 text-right font-black text-slate-900">{item.orderWeight}</td>
                                                                    <td className="p-4 text-center">
                                                                        <span className="px-2.5 py-1 bg-slate-100 text-slate-600 rounded-lg font-mono font-bold tracking-tight border border-slate-200">
                                                                            {item.width_Height}
                                                                        </span>
                                                                    </td>
                                                                    <td className="p-4 text-center">
                                                                        {item.is_Bloster ? (
                                                                            <div className="inline-flex items-center gap-1.5 px-2 py-1 bg-blue-50 rounded-lg group/pop relative">
                                                                                <span className="w-1.5 h-1.5 rounded-full bg-blue-600 animate-pulse"></span>
                                                                                <span className="text-blue-700 font-black text-[10px]">#{item.bloster_No}</span>
                                                                                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-slate-900 text-white text-[10px] rounded opacity-0 group-hover/pop:opacity-100 transition-opacity pointer-events-none whitespace-nowrap">
                                                                                    Bloster Included
                                                                                </div>
                                                                            </div>
                                                                        ) : (
                                                                            <span className="text-slate-200">NO</span>
                                                                        )}
                                                                    </td>
                                                                    <td className="p-4 text-center">
                                                                        <div className="flex items-center justify-center gap-2">
                                                                            {item.is_Pdf_Upload && (
                                                                                <a
                                                                                    href={`https://hline.blob.core.windows.net/hlinefiles/Extrusion/DiePoPDFFiles/${fileNameBase}.pdf`}
                                                                                    target="_blank"
                                                                                    rel="noopener noreferrer"
                                                                                    className="w-8 h-8 flex items-center justify-center bg-red-50 text-red-500 rounded-lg hover:bg-red-500 hover:text-white transition-all shadow-sm"
                                                                                    title="PDF Drawing"
                                                                                >
                                                                                    <FaEye size={14} />
                                                                                </a>
                                                                            )}
                                                                            {item.is_Image_Upload && (
                                                                                <a
                                                                                    href={`https://hline.blob.core.windows.net/hlinefiles/Extrusion/DiePoImageFiles/${fileNameBase}.jpg`}
                                                                                    target="_blank"
                                                                                    rel="noopener noreferrer"
                                                                                    className="w-8 h-8 flex items-center justify-center bg-blue-50 text-blue-500 rounded-lg hover:bg-blue-500 hover:text-white transition-all shadow-sm"
                                                                                    title="Die Image"
                                                                                >
                                                                                    <FaEye size={14} />
                                                                                </a>
                                                                            )}
                                                                            {!item.is_Pdf_Upload && !item.is_Image_Upload && (
                                                                                <span className="text-slate-200 text-[10px] font-black italic">NO ASSETS</span>
                                                                            )}
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>
                                            </div>
                                        );
                                    }}
                                />
                            </div>
                        )}
                    </div>
                ) : (
                    <div style={{ height: "calc(100vh - 260px)" }}>
                        <PendingInwardTab />
                    </div>
                )}
            </div>

            <ConfirmDeleteModal
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                onConfirm={handleConfirmDelete}
                loading={loading}
            />

            {errorModalOpen && (
                <ErrorModal
                    message={errorMessage}
                    subMessage={errorSubMessage}
                    onClose={() => setErrorModalOpen(false)}
                />
            )}

            {isModelOpen && (
                <DiePurchaseOrderForm
                    initialData={formData}
                    key={JSON.stringify(formData)} // Force re-render on data change
                    onCancel={() => {
                        setIsModelOpen(false);
                        setFormData(blankForm);
                    }}
                    onSubmit={handleSubmit}
                    isLoading={loading}
                    isEditing={isEditing}
                />
            )}
        </>
    );
};

export default DiePurchaseOrderMaster;
